/**
 * Shared constants for the solution-led marketing pages.
 *
 * These began life as `/newhomepage` and `/pricingnew`, unlisted candidates run
 * alongside the live pages so the message could be judged before committing.
 * They became `/` and `/pricing` on 2026-08-02; the previous versions moved to
 * `/archive/*`.
 */

/**
 * Where every "start with a call" CTA points.
 *
 * The contact form, and that is a settled decision rather than a gap: reviewed
 * again on 2026-08-06 (Allan) against Calendly, Microsoft Bookings and Google
 * appointment schedules, and none was adopted. Adding a booking tool later
 * remains a one-line change here plus `PRIMARY_CTA_LABEL` - `SolutionCta`
 * already renders an external URL as a plain anchor and an internal path as a
 * router link, so no call site changes when the target's kind does.
 */
export const PRIMARY_CTA_TARGET = "/contact";

/** Reads as an honest description of what actually happens on click. */
export const PRIMARY_CTA_LABEL = "Start with a call";

/**
 * `source` values sent to `POST /api/v1/public/contact`, so leads are
 * distinguishable in whatever inbox or CRM receives them. Changing these breaks
 * attribution on existing leads.
 *
 * The qualifier posts directly. The candidate pages route through the live
 * `/contact`, carrying their tag as a `?source=` parameter - which is what
 * makes the A/B measurable at lead level rather than only in GA click counts.
 */
export const CONTACT_SOURCE_TAGS = {
  qualifier: "marketing:poc-qualifier",
  home: "marketing:home",
  pricing: "marketing:pricing",
} as const;

/**
 * Tags emitted while the pages were A/B candidates, before they became `/` and
 * `/pricing` on 2026-08-02. No longer sent, but still accepted: a link shared
 * during that period should still be attributed rather than silently falling
 * back to the generic tag. Leads in the CRM carrying these came from the
 * candidate pages.
 */
const LEGACY_SOURCE_TAGS = [
  "marketing:newhomepage",
  "marketing:pricingnew",
] as const;

/**
 * The allowlist `/contact` validates its `?source=` against.
 *
 * A query parameter flows straight into the CRM, so an unvalidated one lets
 * anyone write arbitrary text into your lead records simply by sharing a
 * crafted link. Unknown values fall back to the page's own default.
 */
const KNOWN_SOURCES: string[] = [
  ...Object.values(CONTACT_SOURCE_TAGS),
  ...LEGACY_SOURCE_TAGS,
];

export function isKnownContactSource(value: unknown): value is string {
  return typeof value === "string" && KNOWN_SOURCES.includes(value);
}

/**
 * Re-exported so the candidate pages import brand copy from one place. The
 * definition lives in `@/lib/brand` alongside the enquiry address and phone
 * number, which the Header, Footer and organisation schema also read.
 */
export { STRAPLINE } from "@/lib/brand";

/** The qualifier's permanent, indexed URL. Survives the homepage switch. */
export const QUALIFIER_PATH = "/proof-of-concept";
